import React from 'react';
import { Card, Row, Col, ListGroup } from 'react-bootstrap';
import { FaCheckCircle, FaTimesCircle, FaCalendarAlt, FaClock, FaExclamationTriangle, FaWalking } from 'react-icons/fa';
import { PieChart, Pie, Cell, Tooltip, ResponsiveContainer, Legend } from 'recharts';
import './Calendar.css';

const AttendanceSummaryCard = ({ summary }) => {
  if (!summary) return null;

  const data = [
    { name: 'Present', value: summary.presentDays, color: '#28a745' },
    { name: 'Absent', value: summary.absentDays, color: '#dc3545' },
    { name: 'Leave', value: summary.leaveDays, color: '#ffc107' },
    { name: 'Half-day', value: summary.halfDays, color: '#f97316' },
    { name: 'Late', value: summary.lateMarks, color: '#ffb300' },
    { name: 'Miss Punch', value: summary.totalMissPunch, color: '#0d6efd' },
    { name: 'Early Going', value: summary.earlyGoing, color: '#c217e8' },
    { name: 'W. Off', value: summary.weeklyOffs, color: '#6c757d' },
  ].filter(item => item.value > 0);

  const displayStats = [
    { label: 'Present Days', value: `${summary.presentDays} (${summary.presentPercent}%)`, icon: <FaCheckCircle className="text-success" /> },
    { label: 'Absent Days', value: `${summary.absentDays} (${summary.absentPercent}%)`, icon: <FaTimesCircle className="text-danger" /> },
    { label: 'Half Days', value: `${summary.halfDays} (${summary.halfDayPercent}%)`, icon: <FaClock style={{ color: '#f97316' }} /> },
    { label: 'Late Marks', value: `${summary.lateMarks} (${summary.lateMarkPercent}%)`, icon: <FaExclamationTriangle className="text-warning" /> },
    { label: 'Miss Punch', value: summary.totalMissPunch, icon: <FaExclamationTriangle className="text-primary" /> },
    { label: 'Weekly Off Days', value: `${summary.weeklyOffs} (${summary.weeklyOffPercent}%)`, icon: <FaCalendarAlt className="text-secondary" /> },
    { label: 'Early Going', value: `${summary.earlyGoing} (${summary.earlyGoingPercent}%)`, icon: <FaWalking style={{ color: '#c217e8' }} /> },
    { label: 'Total Work Time', value: summary.totalMonthHours, icon: <FaClock className="text-primary" /> },
    { label: 'Total Overtime', value: summary.totalMonthOvertime, icon: <FaClock style={{ color: '#f97316' }} /> },
  ];

  return (
    <Card id="attendance-analysis-card" className="attendance-summary-card shadow-sm border-0">
      <Card.Body>
        <Card.Title className="fw-bold mb-1 text-primary">Attendance Analysis</Card.Title>
        <p className="text-muted small mb-4">Base: {summary.totalWorkingDays} Working Days</p>

        <div id="attendance-pie-container" style={{ width: '100%', height: 200 }} className="mb-0">
          <ResponsiveContainer>
            <PieChart margin={{ top: 0, right: 0, bottom: 0, left: 0 }}>
              <Pie
                data={data}
                cx="50%"
                cy="50%"
                innerRadius={50}
                outerRadius={75}
                paddingAngle={5}
                dataKey="value"
                label={false}
              >
                {data.map((entry, index) => (
                  <Cell
                    key={`cell-${index}`}
                    fill={entry.color}
                    stroke="none"
                  />
                ))}
              </Pie>
              <Tooltip
                contentStyle={{ borderRadius: '10px', border: 'none', boxShadow: '0 4px 12px rgba(0,0,0,0.1)' }}
              />
            </PieChart>
          </ResponsiveContainer>
        </div>

        {/* Custom dots legend below the pie chart */}
        <div className="d-flex flex-wrap justify-content-center gap-2 mt-2 small">
          <div className="d-flex align-items-center" style={{ fontSize: '0.7rem' }}><div style={{ width: 10, height: 10, borderRadius: '50%', backgroundColor: '#28a745', marginRight: 4 }}></div> Present</div>
          <div className="d-flex align-items-center" style={{ fontSize: '0.7rem' }}><div style={{ width: 10, height: 10, borderRadius: '50%', backgroundColor: '#dc3545', marginRight: 4 }}></div> Absent</div>
          <div className="d-flex align-items-center" style={{ fontSize: '0.7rem' }}><div style={{ width: 10, height: 10, borderRadius: '50%', backgroundColor: '#ffc107', marginRight: 4 }}></div> Leave</div>
          <div className="d-flex align-items-center" style={{ fontSize: '0.7rem' }}><div style={{ width: 10, height: 10, borderRadius: '50%', backgroundColor: '#f97316', marginRight: 4 }}></div> Half Day</div>
          <div className="d-flex align-items-center" style={{ fontSize: '0.7rem' }}><div style={{ width: 10, height: 10, borderRadius: '50%', backgroundColor: '#ffb300', marginRight: 4 }}></div> Late</div>
          <div className="d-flex align-items-center" style={{ fontSize: '0.7rem' }}><div style={{ width: 10, height: 10, borderRadius: '50%', backgroundColor: '#0d6efd', marginRight: 4 }}></div> Miss</div>
          <div className="d-flex align-items-center" style={{ fontSize: '0.7rem' }}><div style={{ width: 10, height: 10, borderRadius: '50%', backgroundColor: '#c217e8', marginRight: 4 }}></div> Early</div>
          <div className="d-flex align-items-center" style={{ fontSize: '0.7rem' }}><div style={{ width: 10, height: 10, borderRadius: '50%', backgroundColor: '#6c757d', marginRight: 4 }}></div> Off</div>
        </div>

        <ListGroup variant="flush" className="summary-list border rounded-3 overflow-hidden shadow-sm mt-3">
          {displayStats.map((stat, index) => (
            <ListGroup.Item
              key={index}
              className="d-flex justify-content-between align-items-center py-1 px-3 bg-white border-bottom"
              style={{ transition: 'all 0.2s', cursor: 'default' }}
            >
              <div className="d-flex align-items-center">
                <span className="me-2" style={{ fontSize: '1rem' }}>{stat.icon}</span>
                <span style={{ fontSize: '0.75rem' }} className="fw-semibold text-dark">{stat.label}</span>
              </div>
              <span className="fw-bold" style={{ color: '#0056b3', fontSize: '0.75rem' }}>{stat.value}</span>
            </ListGroup.Item>
          ))}
        </ListGroup>

        <div className="mt-3 p-3 border-0 rounded-4" style={{ backgroundColor: 'rgba(0, 86, 179, 0.05)', border: '1px solid rgba(0, 86, 179, 0.1)' }}>
          <h6 className="fw-bold mb-2 small text-uppercase border-bottom border-primary border-opacity-25 pb-1 d-flex align-items-center" style={{ color: '#0056b3', fontSize: '0.8rem' }}>
            <FaClock className="me-2" style={{ color: '#0056b3' }} /> Rule Compliance
          </h6>
          <div className="d-flex flex-wrap gap-2 justify-content-between mt-2">
            <div className="d-flex flex-column align-items-start">
              <span className="text-muted fw-semibold mb-1" style={{ fontSize: '0.6rem' }}>SHIFT</span>
              <span className="fw-bold bg-white border px-2 py-0 rounded shadow-sm text-primary" style={{ fontSize: '0.7rem' }}>09:30-18:30</span>
            </div>
            <div className="d-flex flex-column align-items-start">
              <span className="text-muted fw-semibold mb-1" style={{ fontSize: '0.6rem' }}>GRACE</span>
              <span className="fw-bold bg-white border px-2 py-0 rounded shadow-sm" style={{ color: '#d97706', fontSize: '0.7rem' }}>9:35 AM</span>
            </div>
            <div className="d-flex flex-column align-items-start">
              <span className="text-muted fw-semibold mb-1" style={{ fontSize: '0.6rem' }}>OFF</span>
              <span className="fw-bold bg-white border px-2 py-0 rounded shadow-sm" style={{ color: '#dc2626', fontSize: '0.7rem' }}>Tue</span>
            </div>
          </div>
        </div>
      </Card.Body>
    </Card>
  );
};

export default AttendanceSummaryCard;
